<template>
  <div class="sc-message--text" :style="messageColors">
    <!-- template>
      <div class="sc-message--toolbox" :style="{background: messageColors.backgroundColor}">
        <button v-if="me && message.id != null && message.id != undefined" @click="edit" :disabled="isEditing">
          <icon-base :color="isEditing? 'black': messageColors.color" width="10" icon-name="edit">
            <icon-edit />
          </icon-base>
        </button>
        <button v-if="me && message.id != null && message.id != undefined" @click="$emit('remove')">
          <icon-base :color="messageColors.color" width="10" icon-name="remove">
            <icon-cross />
          </icon-base>
        </button>
        <slot name="text-message-toolbox" :message="message" :me="me">
        </slot>
      </div>
    </!-->
    <slot name="label"/>
    <slot :message="message" :messageText="messageText" :messageColors="messageColors" :me="me">
      <!-- div, not p: messageText may contain block elements (<p>, <br>) from marked -->
      <div class="sc-message--text-content" v-html="messageText"></div>
      <p v-if="message.data.meta" class='sc-message--meta' :style="{color: messageColors.color}">{{message.data.meta}}</p>
      <p v-if="message.isEdited" class='sc-message--edited'>
        <icon-base width="10" icon-name="edited">
          <icon-edit />
        </icon-base> edited
      </p>
    </slot>
  </div>
</template>

<script>
import IconBase from '../icons/IconBase.vue'
import IconEdit from '../icons/icons/IconEdit.vue'
import escapeGoat from 'escape-goat'
import Autolinker from 'autolinker'
import DOMPurify from 'dompurify'
import marked from 'marked'
const fmt = require('msgdown')

// Private-use character that survives escape + marked; replaced with <br> before render.
// Not visible when handled correctly — leaks as "tofu" squares if replacement is skipped.
const BLANK_LINE_MARKER = '\uE000'

// marked collapses consecutive blank lines (\n\n\n\n\n\n becomes \n\n), so encode extra spacing
// before markdown runs. blankLines = newlines - 1 (e.g. \n\n = 1 blank line, \n\n\n = 2).
const preserveExtraBlankLines = (text) => {
  return text.replace(/\n{2,}/g, (match) => {
    const blankLines = match.length - 1
    const extraMarkers = blankLines - 1
    if (extraMarkers <= 0) return '\n\n'
    return '\n\n' + BLANK_LINE_MARKER.repeat(extraMarkers) + '\n\n'
  })
}

// Bot markdown often omits newlines and uses inline "- **Label:**" segments on one line.
const normalizeInlineMarkdownLists = (text) => {
  return text
    .replace(/\*\*- \*\*/g, '**\n- **')
    .replace(/:\s{2}- /g, ':\n  - ')
    .replace(/([^\n])\s{2}- /g, '$1\n  - ')
    .replace(/([^\n])- \*\*/g, '$1\n- **')
}

// Bot messages often use plain-text numbered lines and "- " prefixes — escape so marked does not
// turn them into HTML lists (which drops hyphens and adds extra vertical space).
const escapePlainTextListLines = (text) => {
  return text
    .replace(/^(\d+)\.(\s)/gm, '$1\\.$2')
    .replace(/^(\s*)- /gm, '$1\\- ')
}

// HTML ignores \n; .sc-message--text p { margin: 0 } removes paragraph gaps — use <br> instead.
// Repeated spaces are preserved via .sc-message--text-content { white-space: pre-wrap } in _chat-widget.scss.
// marked wraps markers in their own <p>, so collapse those into the correct <br> count first.
// Do not convert newlines between block tags to <br> — that duplicates spacing from block layout.
const newlinesToBreaks = (html) => {
  const markerParagraph = new RegExp(`</p>\\s*<p>(${BLANK_LINE_MARKER}+)</p>\\s*<p>`, 'g')
  return html
    .replace(markerParagraph, (_, markers) => '</p>' + '<br>'.repeat(markers.length + 1) + '<p>')
    .replace(/>\s*[\r\n]+\s*</g, '><')
    .replace(/<\/p><p>/g, '</p><br><p>')
    .replace(/\n/g, '<br>')
}

export default {
  props: {
    message: {
      type: Object,
      required: true
    },
    messageColors: {
      type: Object,
      required: true
    },
    messageStyling: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    messageText () {
      if (this.message.direction === 'sent') {
        // Sent text skips markdown; convert newlines directly to <br> for display.
        return DOMPurify.sanitize(newlinesToBreaks(this.message.data.text))
      }

      const anchorTagRegex = /<a\s+href=["']([^"']+)["'][^>]*>([^<]+)<\/a>/g

      // Must run before marked — markdown collapses extra blank lines otherwise.
      const textToProcess = escapePlainTextListLines(
        preserveExtraBlankLines(
          normalizeInlineMarkdownLists(
            this.message.data.text.replace(anchorTagRegex, '[$2]($1)')
          )
        )
      )
      const escaped = escapeGoat.escape(textToProcess)
      const markdownLinkOrImageRegex = /!?\[(.*?)\]\((.*?)(?:\)|…)/g
      const markdownLinksToHtml = escaped.replace(markdownLinkOrImageRegex, (match, text, url) => {
        const supportedImageFormats = ['.gif', '.jpg', '.jpeg', '.png', '.tiff', '.webp']
        return supportedImageFormats.some(ext => url.includes(ext))
          ? `<img src="${url}" alt="${text}" class="chat-image"/>`
          : `<a href="${url}" target="_blank" rel="noopener noreferrer nofollow" class="chat-link">${text}</a>`
      })
      const urlStringsToAnchor = Autolinker.link(this.messageStyling ? fmt(markdownLinksToHtml) : markdownLinksToHtml, {
        className: 'chat-link',
        truncate: { length: 50, location: 'smart' }
      })
      const htmlString = marked.parse(urlStringsToAnchor, { breaks: true }).replace(/\n+$/, '') // marked always appends a trailing \n
      return DOMPurify.sanitize(newlinesToBreaks(htmlString), { ADD_ATTR: ['target'] }) // DOMPurify keeps <br>; it does not strip newlines
    },
    me () {
      return this.message.author === 'client'
    }
  },
  components: {
    IconBase,
    IconEdit
  }
}
</script>
